import React, { useState } from 'react'
import { Modal, Row, Col, Table, Button, message } from 'antd'
import { useHistory } from 'react-router-dom'
import dayjs from 'dayjs'
import { CloseOutlined, InfoCircleFilled } from '@ant-design/icons'
import * as com from "../../../Common.js";
import AutoProgress from '../../../DocumentChecklist/AutoProgress';
import { Upload } from 'antd'
import { getLoanAmount as getHardMoneyLoanLoanAmount } from "../../../HardMoneyLoan/HardMoneyLoan/Overview";
import { importErrorMessage, createLoanErrorMessage, GENERIC_IMPORT_ERROR, GENERIC_CREATE_LOAN_ERROR, SESSION_EXPIRED_ERROR } from './errors'
import { track } from 'statsig'
import { STATSIG } from 'statsig/events'
import './index.css'
const { Dragger } = Upload
export default function ImportMISMOFile(props) {
    const {
        open, onCancel
    } = props
    const history = useHistory()
    const [applicationData, setApplicationData] = useState({})
    // Every borrower the file declared. applicationData carries only the first
    // two, because it has fields for exactly two.
    const [borrowers, setBorrowers] = useState([])
    // Who holds each asset and liability, as indexes into borrowers. The
    // records cannot carry it: their attribution only tells borrower from
    // coborrower, so a joint account between the first and third has nowhere
    // to say so.
    const [recordOwners, setRecordOwners] = useState({ accountOwners: [], liabilityOwners: [], giftOwners: [] })
    // Each borrower's employers' addresses, field by field. Addresses are
    // stored in their own columns now, and one line cannot be split back into
    // them without guessing, so the parts are carried from the parse.
    const [employerAddresses, setEmployerAddresses] = useState([])
    // Who is on title to the subject property, as indexes into borrowers.
    // The subject has no owner list of its own to carry it.
    const [subjectOwners, setSubjectOwners] = useState([])
    // The part of the 1003's manufactured-home section applicationData has no
    // field for: the park address in parts, each section's serial number and
    // the park's telephone. null when the file has none.
    const [manufacturedHome, setManufacturedHome] = useState(null)
    const [confirmLoading, setConfirmLoading] = useState(false)
    const [loanID, setLoanID] = useState("")
    const [fileName, setFileName] = useState("")
    const [formList, setFormList] = useState([])
    const [tableData, setTableData] = useState([])
    const [isExistingBorrower, setIsExistingBorrower] = useState(false)
    const [existingBorrower, setExistingBorrower] = useState("")
    const [uploadStatus, setUploadStatus] = useState('not uploaded')
    const [uploadError, setUploadError] = useState('')
    const modalCancel = () => {
        setIsExistingBorrower(false)
        setTableData([])
        setBorrowers([])
        setRecordOwners({ accountOwners: [], liabilityOwners: [], giftOwners: [] })
        setSubjectOwners([])
        setManufacturedHome(null)
        setUploadStatus('not uploaded')
        setUploadError('')
        onCancel()
    }
    const handleBack = () => {
        if (uploadStatus === 'uploadSuccess') {
            setIsExistingBorrower(false)
            setTableData([])
            setBorrowers([])
            setRecordOwners({ accountOwners: [], liabilityOwners: [], giftOwners: [] })
            setSubjectOwners([])
            setManufacturedHome(null)
            setUploadStatus('not uploaded')
            setUploadError('')
        } else if (uploadStatus === 'importSuccess') {
            setUploadStatus('uploadSuccess')
        }
    }
    const createLoan = () => {
        setConfirmLoading(true)
        let token = sessionStorage.getItem('ZeitroA')
        let tosend = JSON.stringify({
            borrowerEmail: applicationData.borrower.email,
            applicationData: applicationData,
            borrowers: borrowers,
            accountOwners: recordOwners.accountOwners,
            liabilityOwners: recordOwners.liabilityOwners,
            giftOwners: recordOwners.giftOwners,
            employerAddresses: employerAddresses,
            subjectOwners: subjectOwners,
            manufacturedHome: manufacturedHome,
        })
        fetch('/los/createloanfrommismo', {
            method: 'POST',
            body: tosend,
            headers: {
                'Content-Type': 'application/json',
                Authorization: "Bearer " + token,
            },
        })
        .then(async (response) => {
            if (!response.ok) {
                setConfirmLoading(false)
                message.error(response.status === 401 ? SESSION_EXPIRED_ERROR : GENERIC_CREATE_LOAN_ERROR)
                return;
            }
            let data = await response.json();
            setConfirmLoading(false)
            if (data.status === "Error") {
                message.error(createLoanErrorMessage(data.error))
                return;
            }
            setLoanID(data.loanID)
            setUploadStatus('importSuccess')
            if (props.statsigSource === 'borrower') {
                track(STATSIG.BORROWER.LOAN_INSERT, 'mismo_file', {
                    type: 'insert',
                    method: 'mismo_file',
                })
            } else if (props.statsigSource === 'loans') {
                track(STATSIG.LOANS.LOAN_INSERT, 'mismo_file', {
                    type: 'insert',
                    method: 'mismo_file',
                })
            }
        })
        .catch((error) => {
            setConfirmLoading(false)
            message.error(GENERIC_CREATE_LOAN_ERROR)
        })
    }
    const handleConfirm = () => {
        if (uploadStatus === 'uploadSuccess') {
            createLoan()
        } else if (uploadStatus === 'importSuccess') {
            window.location.href = `/app/borrower/${loanID}`
        }
    }
    // coborrowerNames lists everyone but the primary. The summary line used to
    // name only applicationData.coborrower, so on a loan with three borrowers
    // the third was missing from the preview entirely.
    const coborrowerNames = (roster) => roster
        .slice(1)
        .map((b) => `${b.firstname || ''} ${b.lastname || ''}`.trim())
        .filter((name) => name !== '')
        .join(', ')

    // coborrowerBirthDates lines up with coborrowerNames, one date per name and
    // in the same order. The row beside it read applicationData.coborrower,
    // which is the second borrower and only ever the second, so a file with
    // three previewed the third's name against nobody's date of birth.
    const coborrowerBirthDates = (roster) => roster
        .slice(1)
        .filter((b) => `${b.firstname || ''} ${b.lastname || ''}`.trim() !== '')
        .map((b) => (b.dateofbirth ? dayjs(b.dateofbirth).format('MM/DD/YYYY') : '-'))
        .join(', ')

    const getLoanAmount = (props) => {
        if (props.product.mortgageapplied === "hardmoneyloan") {
            return getHardMoneyLoanLoanAmount(props)
        }

        if (props.selection.loansize === "") {
            return "-"
        }
        return "$" + com.commaize(props.selection.loansize)
    }
    const draggerProps = {
        name: 'file',
        showUploadList: false,
        className: 'import-mismo-dragger',
        maxCount: 1,
        onChange(info) {
            const uploadFileName = info.file.name.substring(0, info.file.name.lastIndexOf('.'));
            setFileName(uploadFileName)
            let token = sessionStorage.getItem('ZeitroA')
            const formData = new FormData()
            formData.append('upload', info.file)
            setUploadStatus('loading')
            setUploadError('')
            setTableData([])
            fetch('/los/importmismo', {
                cache: 'no-cache',
                method: 'POST',
                body: formData,
                headers: {
                    Authorization: 'Bearer ' + token,
                },
            })
            .then(async (response) => {
                if (response.status !== 200) {
                    const body = await response.text()
                    let errMessage = body
                    try {
                        errMessage = JSON.parse(body).errMessage
                    } catch (e) {
                        // non-JSON body, e.g. http.Error from the forwarding handler
                    }
                    setUploadError(response.status === 401 ? SESSION_EXPIRED_ERROR : importErrorMessage(errMessage))
                    setUploadStatus('uploadFailed')
                    return;
                }
                let resp = await response.json();
                // Business errors come back as HTTP 200 with a non-zero code and no data.
                if (!resp || !resp.data || (typeof resp.code === 'number' && resp.code !== 0)) {
                    setUploadError(importErrorMessage(resp && resp.errMessage))
                    setUploadStatus('uploadFailed')
                    return;
                }
                setIsExistingBorrower(resp.data.existedBorrowerEmail !== '')
                if (resp.data.existedBorrowerEmail !== '') {
                    setExistingBorrower(`${resp.data.existedBorrowerName}${resp.data.existedCoBorrowerName !== '' ? ', ' + resp.data.existedCoBorrowerName : ''}`)
                }
                let applicationData = resp.data.applicationData
                // The server sends the whole roster; older responses only had
                // the two on applicationData, so fall back to those.
                const roster = Array.isArray(resp.data.borrowers) && resp.data.borrowers.length > 0
                    ? resp.data.borrowers
                    : [applicationData.borrower, applicationData.coborrower].filter((b) => b && b.firstname)
                setBorrowers(roster)
                setEmployerAddresses(resp.data.employerAddresses || [])
                setSubjectOwners(Array.isArray(resp.data.subjectOwners) ? resp.data.subjectOwners : [])
                setManufacturedHome(resp.data.manufacturedHome || null)
                setRecordOwners({
                    accountOwners: resp.data.accountOwners || [],
                    liabilityOwners: resp.data.liabilityOwners || [],
                    giftOwners: resp.data.giftOwners || [],
                })
                let form = [
                    {
                        key: 'borrower',
                        label: 'Primary borrower',
                        value: `${applicationData.borrower.firstname ? applicationData.borrower.firstname + ' ' + applicationData.borrower.lastname  : '-'}`,
                    },
                    {
                        key: 'coborrower',
                        label: 'Co-borrower(s)',
                        value: coborrowerNames(roster) || '-',
                    },
                    {
                        key: 'dateOfBirth',
                        label: 'Date of birth',
                        value: `${applicationData.borrower.dateofbirth ? dayjs(applicationData.borrower.dateofbirth).format('MM/DD/YYYY') : '-'}`,
                    },
                    {
                        key: 'coborrowerDateOfBirth',
                        label: 'Date of birth',
                        value: coborrowerBirthDates(roster) || '-',
                    },
                    {
                        key: 'loanPurpose',
                        label: 'Loan purpose',
                        value: `${applicationData.property.purpose ? com.capitalize(applicationData.property.purpose) : '-'}`,
                    },
                    {
                        key: 'loanAmount',
                        label: 'Loan amount',
                        value: `${getLoanAmount(applicationData)}`,
                    },
                    {
                        key: 'propertyType',
                        label: 'Property type',
                        value: `${applicationData.property.propertytype ? com.capitalize(applicationData.property.propertytype) : '-'}`,
                    },
                    {
                        key: 'propertyAddress',
                        label: 'Property address',
                        value: `${applicationData.property.address ? applicationData.property.address : '-'}`,
                    }
                ]
                // One row per borrower. This used to be two hardcoded blocks
                // reading applicationData.borrower and .coborrower, so a file
                // with three borrowers previewed as two and the third arrived
                // unannounced.
                setTableData(roster
                    .filter((b) => b.firstname || b.lastname)
                    .map((b, idx) => ({
                        key: String(idx + 1),
                        role: idx === 0 ? 'borrower' : 'coborrower',
                        name: `${b.firstname} ${b.lastname}`.trim(),
                        phone: b.homephone ? com.fixPhoneInput(b.homephone) : '-',
                        email: b.email ? b.email : '-',
                    })))
                setApplicationData(applicationData)
                setFormList(form)
                setUploadStatus('uploadSuccess')
            })
            .catch((error) => {
                setUploadError(GENERIC_IMPORT_ERROR)
                setUploadStatus('uploadFailed')
                message.error('The file you uploaded cannot be read. Please upload again.')
            })
        },
        onDrop(e) {
            
        },
        beforeUpload: (file) => {
            const allowedFileTypes = ['text/xml']
            const fileType = file.type
            const isAllowed = allowedFileTypes.includes(fileType)
            if (!isAllowed) {
                message.warning('You can only upload xml files.')
                return Upload.LIST_IGNORE
            }
            return false
        },
    }
    const summaryList = formList.filter((item) => ['loanAmount', 'loanPurpose', 'propertyAddress'].includes(item.key))
    const confirmStatus = () => {
        if (uploadStatus === 'uploadSuccess' || uploadStatus === 'importSuccess') {
            return false
        }
        return true
    }
    const footer = uploadStatus === 'not uploaded' || uploadStatus === 'uploadFailed' ? [
        <Button className="mismo-btn-confirm" type="primary" loading={confirmLoading} disabled={confirmStatus()} onClick={handleConfirm}>{uploadStatus === 'importSuccess' ? 'Continue to application' : 'Confirm upload'}</Button>
    ] : [
        <Button className="mismo-btn-back" type='link' onClick={handleBack}>Back</Button>,
        <Button className="mismo-btn-confirm" type="primary" loading={confirmLoading} disabled={confirmStatus()} onClick={handleConfirm}>{uploadStatus === 'importSuccess' ? 'Continue to application' : 'Confirm upload'}</Button>
    ]
    const columns = [
        {
          title: 'Name',
          dataIndex: 'name',
          key: 'name',
          render: (text, record) => (
            <div>
                <div style={{fontWeight: 500}}>{text}</div>
                <div className={`import-mismo-borrower-tag ${record.role === 'coborrower' ? 'is-coborrower' : isExistingBorrower ? 'is-existing' : 'is-new'}`}>
                    {record.role === 'coborrower' ? 'Co-borrower' : isExistingBorrower ? 'Existing borrower' : 'New borrower'}
                </div>
            </div>
          ),
        },
        {
          title: 'Phone number',
          dataIndex: 'phone',
          key: 'phone',
        },
        {
          title: 'E-mail',
          dataIndex: 'email',
          key: 'email',
        },
    ]
    return (
        <>
            <Modal
                className={`import-mismo-modal ${footer.length === 1 ? 'no-back-modal' : ''}`}
                styles={{ mask: { backgroundColor: 'rgba(0, 0, 0, 0.75)' } }}
                maskClosable={true}
                width={974}
                open={open}
                centered={true}
                title=""
                onCancel={modalCancel}
                footer={footer}
            >
                <div className='import-mismo-title'>Import MISMO file</div>
                <div className='import-mismo-content'>
                    {
                        uploadStatus === 'importSuccess' ? (
                            <>
                                <div className='import-mismo-success-title'>
                                    {isExistingBorrower
                                        ? `Your file has been successfully imported. A new application was created for ${existingBorrower ? existingBorrower : 'an existing borrower'}, who was already in your borrower list.`
                                        : 'Your file has been successfully imported. Following new borrower information has been added to your borrower list.'}
                                </div>
                                <Row className="import-mismo-summary">
                                    {summaryList.map(item => (
                                        <Col key={item.key} md={8} span={24}>
                                            <div className='import-mismo-summary-label'>{item.label}</div>
                                            <div className='import-mismo-summary-value'>{item.value}</div>
                                        </Col>
                                    ))}
                                </Row>
                                <Table
                                    className="import-mismo-borrower-table"
                                    pagination={false}
                                    columns={columns}
                                    dataSource={tableData}
                                />
                            </>
                        ) : (
                            <>
                                {
                                    uploadStatus !== 'importSuccess' && (
                                        <div className='upload-file-wrap'>
                                            {
                                                (uploadStatus === 'uploadSuccess' || uploadStatus === 'uploadFailed') && (
                                                    <>
                                                        <div className={`${uploadStatus === 'uploadFailed' ? 'read-failed' : '' } file-card`}>
                                                            <div><img src={`/images/mismo-file-icon${uploadStatus === 'uploadFailed' ? '-red' : ''}.svg`} style={{width: 24, height: 24, marginRight: 6}} /><span>{fileName}</span></div>
                                                            {
                                                                uploadStatus === 'uploadSuccess' ? (
                                                                    <div style={{width: 24, height: 24, marginLeft: 6, cursor: 'pointer', fontSize: 10, color: '#6E6E70'}} onClick={handleBack}><CloseOutlined /></div>
                                                                ) : (
                                                                    <InfoCircleFilled style={{fontSize: 18, color: '#8D1313', marginRight: 12}} />
                                                                )
                                                            }
                                                        </div>
                                                        {
                                                            uploadStatus === 'uploadSuccess' ? (
                                                                <div className='file-cancel-btn' onClick={handleBack}>Cancel</div>
                                                            ) : (
                                                                <div className='file-read-failed-text'>{uploadError || GENERIC_IMPORT_ERROR}</div>
                                                            )
                                                        }
                                                    </>
                                                )
                                            }
                                        </div>
                                    )
                                }
                                <div className='upload-file-content' style={uploadStatus === 'uploadSuccess' ? {minHeight: 'auto'} : {}}>
                                    {
                                        (uploadStatus === 'not uploaded' || uploadStatus === 'uploadFailed') && (
                                            <Dragger {...draggerProps}>
                                                <div>
                                                    <div className="ant-upload-text">Drag and drop a MISMO file here or</div>
                                                    <div><Button className="mismo-upload" type="primary">Upload from files</Button></div>
                                                    <div className="ant-upload-hint" style={{color: '#6E6E70'}}>
                                                        Only one file at a time
                                                    </div>
                                                </div>
                                            </Dragger>
                                        )
                                    }
                                    {
                                        uploadStatus === 'loading' && (
                                            <div style={{height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column'}}>
                                                <AutoProgress 
                                                    type="circle"
                                                    percent="auto"
                                                    strokeWidth={10} 
                                                    trailColor="#BDCBFB" 
                                                    strokeColor="#325CEB"
                                                    size={100}
                                                    format={(percent) => <span style={{fontSize: 20, fontFamily: 'Inter', color: '#325CEB', fontWeight: 600}}>{percent.toFixed(0)}%</span>}
                                                />
                                                <div style={{fontFamily: 'Inter', fontSize: 16, fontWeight: 500, lineHeight: 'normal', marginTop: 16}}>Uploading...</div>
                                            </div>
                                        )
                                    }
                                    {
                                        uploadStatus === 'uploadSuccess' && (
                                            <Row>
                                                {
                                                    formList.map(item => (
                                                        <Col className='upload-file-item' key={item.key} md={12} span={24}>
                                                            <div>
                                                                <div className='upload-file-label'>{item.label}</div>
                                                                <div className='upload-file-value'>{item.value}</div>
                                                            </div>
                                                        </Col>
                                                    ))
                                                }
                                            </Row>
                                        )
                                    }
                                </div>
                                {
                                    isExistingBorrower && (
                                        <div className='upload-file-match'>
                                            <div className='upload-file-match-text'>
                                                <div>Create new application for matching existing borrower:</div>
                                                {(uploadStatus === 'uploadSuccess' || uploadStatus === 'importSuccess') && <div style={{color: '#325CEB'}}>{existingBorrower}</div>}
                                            </div>
                                        </div>
                                    )
                                }
                            </>
                        )
                    }
                </div>
            </Modal>
        </>
    )
}
